import React, { createRef, CSSProperties } from 'react';
import { IEntity, IEntityListState, EntityListState, IEntityChildren, IBaseComponentProps, IEntityDescription1 } from '../types';
import styles from '../styles/cr.module.scss';
import { FilteredList } from './cr/FilteredList';
import { ConfirmDialog } from './cr/ConfirmDialog';
import { ListCommandBar } from './cr/ListCommandBar';
import { MessageDialog } from './cr/MessageDialog';
import { CrLoadingOverlay } from './cr/CrLoadingOverlay';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { DateService } from '../services';
import { IColumn, IObjectWithKey, Selection } from '@fluentui/react/lib/DetailsList';
import { IUseApiProps } from './useApi';
import { IWithErrorHandlingProps } from './withErrorHandling';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { Gantt, Willow, Tooltip } from "@svar-ui/react-gantt";
import "@svar-ui/react-gantt/all.css";
import { GanttZoomLevel } from '../svargantt/svarGanttConfig';
import { FullscreenWrapper } from './FullscreenWrapper';

export interface IEntityListProps<T extends IEntity> extends IBaseComponentProps, IWithErrorHandlingProps, IUseApiProps {
    entity: string;
    entityName: { Plural: string, Singular: string };
    formTypePage?: boolean;
    barColorBeforeTitle?: string;
    hideHeading?: boolean;
    listAdditionalStyle?: CSSProperties;
    addChild?: { Name: string };
    enableShowHideClosedEntities?: boolean;
    columns: IColumn[];
    loadListItems: (showClosedEntities: boolean) => Promise<T[]>;
    mapEntitiesToListItems: (entities: T[]) => IObjectWithKey[];
    entityForm: (showForm: boolean, entityId: number, onSaved: () => void, onCancelled: (refreshData?: boolean) => void, entityDescription1: string, entityDescription1DirectorateID: number, selectedEntity: T) => React.ReactElement;
    form2?: (showForm: boolean, entityId: number, onSaved: () => void, onCancelled: (refreshData?: boolean) => void, selectedEntity: T) => React.ReactElement;
    viewForm?: (showForm: boolean, entityId: number, onClosed: () => void, entityDescription1: string) => React.ReactElement;
    childEntityForm?: (showForm: boolean, parentEntityId: number, onSaved: () => void, onCancelled: () => void) => React.ReactElement;
    onCheckDeleteCustomMsg?: (entityId: number, selectedEntity: T) => React.ReactElement;
    htmlMsgOnDeleteConfirm?: (entityId: number, selectedEntity: T) => React.ReactElement;
    onCheckDelete: (entityId: number) => Promise<IEntityChildren[]>;
    onDelete: (entityId: number) => Promise<void>;

    onUpdateDates?: (
        entityId: number,
        startDate: Date,
        endDate: Date
    ) => Promise<void>;


    onChange?: () => void;
    disableAdd?: boolean;
    disableDelete?: (entity: T) => boolean;
    addMultiple?: boolean;
    additionalItemsOnSelection?: (entity: T) => ICommandBarItemProps[];
    preFarItems?: (entity: T) => ICommandBarItemProps[];
    veryFirstItems?: (entity: T) => ICommandBarItemProps[];
    preFarItemsOnSelection?: (entity: T) => ICommandBarItemProps[];
    showEntityDescription1?: boolean;
    loadEntityDescription1?: () => Promise<IEntityDescription1>;
    onEntityDescriptionChange?: (desc: IEntityDescription1) => void;
    defaultView?: 'list' | 'gantt'; // new prop
    isMobile?: boolean;

    //gantt props
    renderGanttPopup?: (entity: T) => string; //old - not used
    renderGanttTooltip?: (data: any) => React.ReactElement;
    showListAndGantt?: boolean;
    enableGanttDrag?: boolean;
    ganttTopOffset?: number;
    ganttColumns?: any[];
    ganttTasks?: any[];
    ganttLinks?: any[];
    ganttZoom?: any;
    defaultGanttZoomLevel?: GanttZoomLevel;
    ganttTaskTypes?: any[];
    ganttHighlightTime?: (date: Date, unit: string) => string;
    buildGanttData?: (entities: T[]) => {
        tasks: any[];
        links?: any[];
    };
    // Gantt behaviour customization
    getTaskId?: (entity: T) => string;
    getEntityIdFromTask?: (task: any) => number | null;
    isTaskDraggable?: (task: any) => boolean;
    isTaskResizable?: (task: any) => boolean;
    onTaskSelected?: (task: any) => void;
    clearSelectionOnNonEntityTask?: boolean;
    startDateField?: string;
    endDateField?: string;
    editableTaskTypes?: string[];

}

export class EntityList<T extends IEntity> extends React.Component<IEntityListProps<T>, IEntityListState<T>> {
    private _selection: Selection;

    constructor(props: IEntityListProps<T>) {
        super(props);
        this.state = new EntityListState<T>();
        this.updateGanttHeight = this.updateGanttHeight.bind(this);

        this._selection = new Selection({
            onSelectionChanged: () => {
                if (this._selection.getSelectedCount() === 1) {
                    const selected = this._selection.getSelection()[0] as any;
                    const key = Number(selected.key);

                    const isViewOnly = selected.IsViewOnly ?? false;

                    const enableView = props.viewForm ? true : false;

                    this.setState({
                        SelectedEntity: key,
                        EnableEdit: !isViewOnly,
                        EnableView: enableView,
                        EnableDelete: !isViewOnly,
                        EnableAddChild: !isViewOnly
                    });
                } else {
                    this.setState({
                        SelectedEntity: null,
                        EnableEdit: false,
                        EnableView: false,
                        EnableDelete: false,
                        EnableAddChild: false
                    });
                }
            }
        });
    }

    public render(): React.ReactElement<IEntityListProps<T>> {
        const {
            enableShowHideClosedEntities,
            entityName,
            addChild,
            columns,
            mapEntitiesToListItems,
            entityForm,
            form2,
            viewForm,
            childEntityForm,
            disableAdd,
            disableDelete,
            formTypePage,
            addMultiple } = this.props;
        const {
            LoadingListData,
            LoadingDeleteCheck,
            ShowClosedEntities,
            HideDeleteDialog,
            HideDeleteDisallowed,
            HideCustomDeleteDisallowed,
            CustomDeleteMsg,
            EnableEdit,
            EnableView,
            EnableDelete,
            EnableAddChild,
            Entities,
            ListFilterText,
            ShowForm,
            ShowForm2,
            ShowViewForm,
            ShowChildForm,
            SelectedEntity,
            SelectedEntityChildren } = this.state;
        const numChildren = this.totalChildren(SelectedEntityChildren);
        const addMultipleItems = addMultiple ? { onAddMultiple: this.addEntity } : {};

        const ganttData = this.props.buildGanttData
            ? this.props.buildGanttData(Entities.Items)
            : { tasks: [], links: [] };

        // Create a copy of the zoom config with default level applied
        const ganttZoomWithDefault = {
            ...this.props.ganttZoom,
            level: this.state.CurrentZoomLevel
        };


        return (
            <div style={{ width: '100%' }} className={`${styles.cr} ${this.props.isFullPage ? styles.crFullPage : ''}`}>
                <CrLoadingOverlay isLoading={LoadingDeleteCheck} opaque={false} />

                {<div style={{ display: (ShowForm && formTypePage) ? 'none' : 'block' }}>
                    <div style={{ display: 'flex' }}>
                        {this.props.barColorBeforeTitle && <div style={{ marginTop: '1px', width: '8px', height: '45px', backgroundColor: this.props.barColorBeforeTitle }}></div>}
                        {this.props.hideHeading !== true && <h2 style={{ flexGrow: 1 }} className={styles.listTitle}>{entityName.Plural}</h2>}
                        {enableShowHideClosedEntities &&
                            <div style={{ paddingTop: '8px' }}>
                                <Toggle
                                    //onText={`Show closed ${entityName.Plural?.toLocaleLowerCase()}?`}
                                    //offText={`Show closed ${entityName.Plural?.toLocaleLowerCase()}?`}
                                    onText={`Show closed?`}
                                    offText={`Show closed?`}
                                    checked={ShowClosedEntities}
                                    onChange={this.onClosedToggleChange}
                                />
                            </div>
                        }
                    </div>

                    <ListCommandBar
                        //className={`${styles.crCommandBarTopBorder}`}
                        showListAndGantt={this.props.showListAndGantt}
                        isMobile={this.props.isMobile}
                        isGanttView={this.state.showGanttView}
                        onGanttZoomIn={this.zoomIn}
                        onGanttZoomOut={this.zoomOut}
                        currentZoomLevel={this.state.CurrentZoomLevel}
                        ganttZoom={this.props.ganttZoom}
                        onToggleView={this.toggleView}
                        onAdd={this.addEntity}
                        {...addMultipleItems}
                        onEdit={this.editEntity}
                        onView={this.viewEntity}
                        onDelete={this.checkDelete}
                        onFilterChange={this.onFilterChange}
                        addDisabled={disableAdd || false}
                        editDisabled={!EnableEdit}
                        viewDisabled={!EnableView}
                        deleteDisabled={!EnableDelete || disableDelete?.(Entities?.Items?.find(i => i.ID === SelectedEntity))}
                        addChildDisabled={!EnableAddChild}
                        onAddChild={this.addEntityChild}
                        addChildName={addChild?.Name}
                        veryFirstItems={this.props.veryFirstItems?.(Entities?.Items?.find(i => i.ID === SelectedEntity))}
                        preFarItems={this.props.preFarItems?.(Entities?.Items?.find(i => i.ID === SelectedEntity))}
                        preFarItemsOnSelection={this.props.preFarItemsOnSelection?.(Entities?.Items?.find(i => i.ID === SelectedEntity))}
                        additionalItemsOnSelection={this.props.additionalItemsOnSelection?.(Entities?.Items?.find(i => i.ID === SelectedEntity))}
                    />

                    {(this.props.showEntityDescription1 === true && !this.props.onEntityDescriptionChange) && (
                        <div style={{ paddingLeft: '10px', paddingTop: '10px', paddingBottom: '15px', fontSize: '14px' }}>
                            <div dangerouslySetInnerHTML={{ __html: this.state.EntityDescription1ForList }} />
                        </div>
                    )}
                    <div
                        data-is-scrollable={this.props.isFullPage ? 'true' : 'false'}
                        className={styles.crFullPageList}
                        style={{
                            position: 'relative',
                            ...this.props.listAdditionalStyle,
                        }}
                    >
                        <CrLoadingOverlay isLoading={LoadingListData} opaque={true} />
                        {
                            this.props.showListAndGantt ?
                                (
                                    this.state.showGanttView /*&& !this.props.isMobile*/ ? (
                                        <Willow>
                                            <div style={{ width: "100%", height: this.props.isMobile ? "auto" : this.state.GanttHeight }}>
                                                <Tooltip
                                                    api={this.state.GanttApi}
                                                    content={this.props.renderGanttTooltip}
                                                >
                                                    <FullscreenWrapper hotkey="ctrl+shift+f">
                                                        <Gantt
                                                            init={(api) => {
                                                                this.setState({ GanttApi: api });

                                                                if (!this.props.enableGanttDrag) {
                                                                    api.intercept("drag-task", () => false);
                                                                    api.intercept("resize-task", () => false);
                                                                    api.intercept("add-link", () => false);
                                                                    return;
                                                                }

                                                                /* =========================================
                                                                   ALLOW DRAG
                                                                ========================================= */
                                                                api.intercept("drag-task", (ev) => {
                                                                    const task = api.getTask(ev.id);
                                                                    if (!task) return false;

                                                                    if (this.props.isTaskDraggable) {
                                                                        return this.props.isTaskDraggable(task);
                                                                    }

                                                                    return true;
                                                                });

                                                                /* =========================================
                                                                   ALLOW RESIZE
                                                                ========================================= */

                                                                api.intercept("resize-task", (ev) => {
                                                                    const task = api.getTask(ev.id);
                                                                    if (!task) return false;

                                                                    if (this.props.isTaskResizable) {
                                                                        return this.props.isTaskResizable(task);
                                                                    }

                                                                    return true;
                                                                });

                                                                /* =========================================
                                                                   OPTIONAL: DISABLE LINKING COMPLETELY
                                                                ========================================= */

                                                                api.intercept("add-link", () => false);


                                                                /* =========================================
                                                                   HANDLE UPDATE
                                                                ========================================= */

                                                                api.on("update-task", (ev) => {
                                                                    const { task } = ev;
                                                                    if (!task) return;

                                                                    const { startDateField, endDateField, editableTaskTypes } = this.props;

                                                                    // Only allow specific task types
                                                                    if (
                                                                        editableTaskTypes &&
                                                                        !editableTaskTypes.includes(task.type)
                                                                    ) return;

                                                                    const originalStart = task._entity?.[startDateField];
                                                                    const originalEnd = task._entity?.[endDateField];

                                                                    const newStart = task.start;
                                                                    const newEnd = task.end;

                                                                    if (!(newStart instanceof Date) || !(newEnd instanceof Date)) return;

                                                                    const isSameStart =
                                                                        originalStart instanceof Date &&
                                                                        originalStart.getTime() === newStart.getTime();

                                                                    const isSameEnd =
                                                                        originalEnd instanceof Date &&
                                                                        originalEnd.getTime() === newEnd.getTime();

                                                                    if (isSameStart && isSameEnd) return;

                                                                    this.onAfterTaskUpdate(task);
                                                                });

                                                                api.on("select-task", (ev) => {
                                                                    const { id } = ev;
                                                                    const task = api.getTask(id);

                                                                    if (!task) return;

                                                                    if (this.props.getEntityIdFromTask) {
                                                                        const entityId = this.props.getEntityIdFromTask(task);

                                                                        if (entityId) {
                                                                            this.setState({
                                                                                SelectedEntity: entityId,
                                                                                EnableEdit: true,
                                                                                EnableView: !!this.props.viewForm,
                                                                                EnableDelete: true,
                                                                                EnableAddChild: true
                                                                            });

                                                                            if (this.props.onTaskSelected) {
                                                                                this.props.onTaskSelected(task);
                                                                            }

                                                                            return;
                                                                        }
                                                                    }

                                                                    if (this.props.clearSelectionOnNonEntityTask) {
                                                                        this.setState({
                                                                            SelectedEntity: null,
                                                                            EnableEdit: false,
                                                                            EnableView: false,
                                                                            EnableDelete: false,
                                                                            EnableAddChild: false
                                                                        });
                                                                    }
                                                                });

                                                            }}
                                                            selected={
                                                                this.state.SelectedEntity && this.props.getTaskId
                                                                    ? [this.props.getTaskId(this.getSelectedEntity())]
                                                                    : []
                                                            }
                                                            tasks={ganttData.tasks}
                                                            links={ganttData.links}
                                                            columns={this.props.isMobile ? [] : this.props.ganttColumns}
                                                            zoom={ganttZoomWithDefault}
                                                            taskTypes={this.props.ganttTaskTypes}
                                                            highlightTime={this.props.ganttHighlightTime}
                                                        />
                                                    </FullscreenWrapper>
                                                </Tooltip>
                                            </div>
                                        </Willow>
                                    )

                                        : (
                                            <FilteredList
                                                columns={columns}
                                                items={{ Timestamp: Entities.Timestamp, Items: mapEntitiesToListItems(Entities.Items) }}
                                                selection={this._selection}
                                                filterText={ListFilterText}
                                                selectionPreservedOnEmptyClick={this.state.SelectionPreservedOnEmptyClick}
                                            />
                                        )
                                ) :
                                (
                                    <FilteredList
                                        columns={columns}
                                        items={{ Timestamp: Entities.Timestamp, Items: mapEntitiesToListItems(Entities.Items) }}
                                        selection={this._selection}
                                        filterText={ListFilterText}
                                        selectionPreservedOnEmptyClick={this.state.SelectionPreservedOnEmptyClick}
                                    />
                                )
                        }


                    </div>
                </div>}



                {ShowForm && entityForm(ShowForm, SelectedEntity, this.entitySaved, this.closePanel, this.state.EntityDescription1ForForm, this.state.EntityDescription1DirectorateID, this.getSelectedEntity())}
                {ShowForm2 && form2(ShowForm2, SelectedEntity, this.entitySaved, this.closePanel, this.getSelectedEntity())}
                {ShowViewForm && viewForm(ShowViewForm, SelectedEntity, this.closeViewPanel, this.state.EntityDescription1ForForm)}
                {ShowChildForm && childEntityForm && childEntityForm(ShowChildForm, SelectedEntity, this.childEntitySaved, this.closeChildPanel)}
                <MessageDialog
                    hidden={HideCustomDeleteDisallowed}
                    title={`${entityName.Singular} cannot be deleted`}
                    handleOk={() => this.setState({ HideCustomDeleteDisallowed: true })}>
                    {CustomDeleteMsg}
                </MessageDialog>
                <MessageDialog
                    hidden={HideDeleteDisallowed}
                    title={`${entityName.Singular} cannot be deleted`}
                    handleOk={this.toggleDeleteDisallowed}>
                    {this.anyChildrenUnadoptable(SelectedEntityChildren)
                        ?
                        <>
                            <p>{entityName.Singular} &apos;{this.getSelectedEntityName()}&apos; has items of the following types belonging to it that cannot be moved or deleted.</p>
                            <p>{SelectedEntityChildren.filter(c => !c.CanBeAdopted).map(c => c.ChildType).join(', ')}</p>
                        </>
                        :
                        <>
                            <p>{entityName.Singular} &apos;{this.getSelectedEntityName()}&apos; has {this.anyChildrenMax(SelectedEntityChildren)
                                ? `${numChildren} or more items` : numChildren > 1 ? `${numChildren} items` : `1 item`} belonging to it:</p>
                            {SelectedEntityChildren.filter(c => c.ChildIDs.length > 0).map(c => <p key={c.ChildType}>{`${c.ChildType} (IDs) - ${c.ChildIDs.join(', ')}`}</p>)}
                            <p>{numChildren > 1 ? 'These' : 'This'} must be deleted or moved from this {entityName.Singular} before it can be deleted.</p>
                        </>
                    }
                </MessageDialog>
                <ConfirmDialog
                    hidden={HideDeleteDialog}
                    title={`Are you sure you want to delete ${this.getSelectedEntityName() || `this ${entityName.Singular.toLowerCase()}`}?`}
                    {...(
                        this.state.HTMLDeleteMsg
                            ? { htmlContent: this.state.HTMLDeleteMsg }
                            : { content: `Deleting a ${entityName.Singular.toLowerCase()} cannot be undone.` }
                    )}
                    confirmButtonText="Delete"
                    handleConfirm={this.deleteEntity}
                    handleCancel={this.toggleDeleteConfirm}
                />

            </div>
        );
    }

    //#region Form initialisation

    private updateGanttHeight() {
        const viewportHeight = window.innerHeight;
        const topOffset = this.props.ganttTopOffset ?? 230;
        const containerHeight = Math.max(400, viewportHeight - topOffset); // optional min height
        this.setState({ GanttHeight: containerHeight });
    }


    public componentDidMount(): void {
        if (this.props.apiConnected) {
            this.loadEntities();
            if (this.props.showListAndGantt) {
                this.setState({
                    CurrentZoomLevel: this.props.defaultGanttZoomLevel ?? this.props.ganttZoom.level ?? 1
                });
                this.updateGanttHeight();
                window.addEventListener("resize", this.updateGanttHeight);
                this.setState({
                    showGanttView: this.props.defaultView === 'gantt'
                })
            }
        }
    }

    public componentWillUnmount(): void {
        if (this.props.showListAndGantt) {
            window.removeEventListener("resize", this.updateGanttHeight);
        }
    }

    public componentDidUpdate(prevProps: IEntityListProps<T>, prevState: Readonly<IEntityListState<T>>): void {
        if (prevProps.apiConnected !== this.props.apiConnected || prevProps.entity !== this.props.entity) {
            this.loadEntities();
        }
    }

    private loadEntities = async (): Promise<void> => {
        const { entityName, loadListItems, showEntityDescription1, loadEntityDescription1 } = this.props;
        this.setState({ LoadingListData: true });
        try {
            if (showEntityDescription1 === true) {
                const entityDescription1: IEntityDescription1 = await loadEntityDescription1();
                this.setState({
                    EntityDescriptionLine1: entityDescription1.DescriptionLine1,
                    EntityDescriptionLine2: entityDescription1.DescriptionLine2,
                    EntityDescription1ForList: entityDescription1.Description1ForList,
                    EntityDescription1ForForm: entityDescription1.Description1ForForm,
                    EntityDescription1DirectorateID: entityDescription1.DirectorateID,
                });

                this.props.onEntityDescriptionChange(entityDescription1);
            }
            const items = await loadListItems(this.state.ShowClosedEntities);
            //console.log(items);
            if (items/* && items.length > 0*/) {
                this.setState({ Entities: { Timestamp: DateService.timestamp(), Items: items } });
            }
        }
        catch (err) {
            this.props.errorHandling?.onError(`Error loading ${entityName.Plural || 'items'} `, err.message);
        } finally {
            this.setState({ LoadingListData: false });
        }
    }


    //#endregion

    //#region Form infrastructure

    private getSelectedEntityName = (): string => {
        const entity = this.state.Entities.Items.find(e => e.ID === this.state.SelectedEntity);
        return entity ? entity.Title : null;
    }

    private getSelectedEntity = (): T => {
        const entity = this.state.Entities.Items.find(e => e.ID === this.state.SelectedEntity);
        return entity ? entity : null;
    }

    private addEntity = (): void => {
        if (this.state.SelectedEntity) {
            this._selection.setKeySelected(this.state.SelectedEntity.toString(), false, false);
        }
        this.setState({ SelectedEntity: null, ShowForm: true, SelectionPreservedOnEmptyClick: true });
    }

    private editEntity = (): void => {
        this.setState({ ShowForm: true, SelectionPreservedOnEmptyClick: true });
    }

    public openEditForm = (entityId: number): void => {
        this.setState({
            SelectedEntity: entityId,
            ShowForm: true,
            SelectionPreservedOnEmptyClick: true
        });
    };

    public openForm2 = (entityId: number): void => {
        this.setState({
            SelectedEntity: entityId,
            ShowForm2: true,
            SelectionPreservedOnEmptyClick: true
        });
    };


    private viewEntity = (): void => {
        this.setState({ ShowViewForm: true });
    }

    private deleteEntity = async (): Promise<void> => {
        const { onDelete } = this.props;
        const { SelectedEntity } = this.state;
        this.props.errorHandling?.clearErrors();
        this.setState({ HideDeleteDialog: true });
        if (SelectedEntity) {
            try {
                await onDelete(SelectedEntity);
                this.loadEntities();
            } catch (err) {
                this.props.errorHandling?.onError(`Error deleting item ${SelectedEntity} `, err.message);
            }
        }
    }

    private addEntityChild = (): void => {
        if (this.state.SelectedEntity) {
            this.setState({ ShowChildForm: true });
        }
    }

    private entitySaved = (): void => {
        this.loadEntities();
        if (this.props.onChange) this.props.onChange();
        this.closePanel();
    }

    private childEntitySaved = (): void => {
        this.loadEntities();
        this.closeChildPanel();
    }

    private closePanel = (refreshData?: boolean): void => {
        this.setState({ ShowForm: false, ShowForm2: false, SelectionPreservedOnEmptyClick: false },
            () => {

            }
        );
    }
    private closeViewPanel = (): void => {
        this.setState({ ShowViewForm: false });
    }

    private closeChildPanel = (): void => {
        this.setState({ ShowChildForm: false });
    }

    private toggleView = (): void => {
        this.setState(prevState => ({
            showGanttView: !prevState.showGanttView
        }));
    }

    private toggleDeleteConfirm = (): void => {
        this.setState({ HideDeleteDialog: !this.state.HideDeleteDialog });
    }

    private toggleDeleteDisallowed = (): void => {
        this.setState({ HideDeleteDisallowed: !this.state.HideDeleteDisallowed });
    }

    private checkDelete = async (): Promise<void> => {

        if (this.props.onCheckDeleteCustomMsg) {
            const customDelMsg: React.ReactElement = this.props.onCheckDeleteCustomMsg(this.state.SelectedEntity, this.getSelectedEntity());
            if (customDelMsg != null) {
                this.setState({ CustomDeleteMsg: customDelMsg, HideCustomDeleteDisallowed: false });
                return;
            }

        }

        this.setState({ LoadingDeleteCheck: true });
        try {
            const children = await this.props.onCheckDelete(this.state.SelectedEntity);
            //console.log('on check delete..', this.state.SelectedEntity);
            if (this.totalChildren(children) > 0) {
                this.setState({ SelectedEntityChildren: children }, this.toggleDeleteDisallowed);
            }
            else {
                if (this.props.htmlMsgOnDeleteConfirm) {
                    const htmlDeleteMsg: React.ReactElement = this.props.htmlMsgOnDeleteConfirm(this.state.SelectedEntity, this.getSelectedEntity());
                    this.setState({ HTMLDeleteMsg: htmlDeleteMsg });
                }
                this.toggleDeleteConfirm();
            }
        } catch (err) {
            this.props.errorHandling?.onError(`Error checking if item can be deleted.`, err.message);
        } finally {
            this.setState({ LoadingDeleteCheck: false });
        }
    }

    private totalChildren = (children: IEntityChildren[]): number => {
        return children.map(c => c.ChildIDs.length).reduce((total, numberOfChildType) => total + numberOfChildType, 0);
    }

    private anyChildrenUnadoptable = (children: IEntityChildren[]): boolean => {
        return children.some(c => !c.CanBeAdopted && c.ChildIDs.length > 0);
    }

    private anyChildrenMax = (children: IEntityChildren[]): boolean => {
        return children.some(c => c.ChildIDs.length === 10);
    }

    private onFilterChange = (value: string): void => {
        this.setState({ ListFilterText: value });
    }

    private onClosedToggleChange = (_, checked: boolean): void => {
        this.setState({ ShowClosedEntities: checked }, this.loadEntities);
    }

    private zoomIn = () => {
        const maxLevel = GanttZoomLevel.MonthDay; // 4

        this.setState(prev => {
            const current = prev.CurrentZoomLevel ?? GanttZoomLevel.Year;
            const next = Math.min(current + 1, maxLevel);

            // console.log("[ZOOM IN]");
            // console.log("Current Level:", current);
            // console.log("Max Allowed Level:", maxLevel);
            // console.log("Next Level:", next);

            return { CurrentZoomLevel: next };
        }, () => {
            //console.log("Updated State Level:", this.state.CurrentZoomLevel);
        });
    };



    private zoomOut = () => {
        const minLevel = GanttZoomLevel.Year; // 0

        this.setState(prev => {
            const current = prev.CurrentZoomLevel ?? GanttZoomLevel.Year;
            const next = Math.max(current - 1, minLevel);

            // console.log("[ZOOM OUT]");
            // console.log("Current Level:", current);
            // console.log("Min Allowed Level:", minLevel);
            // console.log("Next Level:", next);

            return { CurrentZoomLevel: next };
        }, () => {
            //console.log("Updated State Level:", this.state.CurrentZoomLevel);
        });
    };


    private onAfterTaskUpdate = async (task: any) => {

        if (!task || !task._entity) {
            console.error("❌ task._entity is missing");
            return;
        }

        const { startDateField, endDateField } = this.props;

        const entityId = task._entity.ID;
        const newStart = task.start;
        const newEnd = task.end;

        if (!(newStart instanceof Date) || !(newEnd instanceof Date)) {
            console.error("❌ Invalid dates", newStart, newEnd);
            return;
        }

        const entities = [...this.state.Entities.Items];
        const index = entities.findIndex(e => e.ID === entityId);

        if (index === -1) {
            console.error("❌ Entity not found in state");
            return;
        }

        entities[index] = {
            ...entities[index],
            [startDateField]: newStart,
            [endDateField]: newEnd
        };

        this.setState({
            Entities: {
                ...this.state.Entities,
                Items: entities
            }
        });


        if (this.props.onUpdateDates) {
            try {
                await this.props.onUpdateDates(entityId, newStart, newEnd);
            } catch (error) {
                console.error("❌ Failed to save dates", error);
            }
        }
    };



    //#endregion
}
